import React, { useEffect, useState } from "react";
import { Avatar, Box, Divider, IconButton, Stack, Tooltip, Typography, Collapse, Chip } from "@mui/material";
import { PhotoCamera, KeyboardArrowDown } from "@mui/icons-material";

import { ActionButton, CardLayout, ToggleButton } from "../v3";

const InfoItem = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
      {value || "-"}
    </Typography>
  </Box>
);

export const ProfileDetails = ({
  title,
  avatarSrc,
  avatarTooltip = "",
  updateProfilePhoto = () => {},
  showStatusToggle = false,
  isActive = false,
  onStatusToggle = () => {},
  buttons = [],
  rows = [],
  defaultExpanded = false,
  style = {},
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  return (
    <CardLayout sx={{ padding: "20px 24px", ...style }}>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ cursor: "pointer" }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {avatarSrc && (
            <Box sx={{ position: "relative", width: 56, height: 56 }}>
              <Avatar src={avatarSrc} sx={{ width: 56, height: 56, backgroundColor: "#F2ECF9" }} />

              {updateProfilePhoto && (
                <Tooltip title={avatarTooltip} arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      updateProfilePhoto();
                    }}
                    sx={{
                      position: "absolute",
                      bottom: -4,
                      right: -4,
                      width: 22,
                      height: 22,
                      backgroundColor: "#fff",
                      boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    <PhotoCamera sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>

            {showStatusToggle && (
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                <Chip
                  label={isActive ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: isActive ? "#E6F4EA" : "#FDECEA",
                    color: isActive ? "#1B5E20" : "#B71C1C",
                  }}
                />

                <ToggleButton checked={isActive} onToggleChange={onStatusToggle} />
              </Stack>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {buttons?.map((button, index) =>
            button ? (
              <Box key={index} onClick={(e) => e.stopPropagation()}>
                <ActionButton
                  buttonText={button?.buttonText}
                  onButtonClick={button?.onClick}
                  buttonIcon={button?.buttonIcon}
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: "capitalize" }}
                />
              </Box>
            ) : null,
          )}

          <IconButton
            size="small"
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <KeyboardArrowDown />
          </IconButton>
        </Stack>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />

        <Stack spacing={3}>
          {rows.map((row, rowIndex) => (
            <Box key={rowIndex} display="flex" gap={4}>
              {row.map((item, colIndex) => (
                <Box key={colIndex} flex={1}>
                  <InfoItem label={item.label} value={item.value} />
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      </Collapse>
    </CardLayout>
  );
};
